import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { paidBy, unpaidTypes } from "./TicketsFormPage";
import { paidAmount, seatsLabel, ticketCount } from "../../utils/pricing";
import { getTicketShareLink } from "../../api/subscription";

// Sending a ticket to the person it was written for: the phone's own share
// sheet where there is one, the clipboard everywhere else. The link is asked
// for only when the button is pressed — a ticket written without an email has
// no short code until somebody needs one.
const ShareTicketButton = ({ eventId, ticket }) => {
  const [state, setState] = useState(null);

  const share = async () => {
    setState("loading");
    try {
      const response = await getTicketShareLink(eventId, ticket?._id);
      const { url, eventTitle, passes } = response?.data?.data || {};
      if (!url) throw new Error("no link");

      // A table or an order of several carries a link per seat; the whole
      // ticket's page is where those are handed out one by one.
      const title = [eventTitle, ticket?.name].filter(Boolean).join(" — ");
      const text = passes?.length > 1 ? `${title} (${passes.length} bilet)` : title;

      if (navigator.share) {
        try {
          await navigator.share({ title, text, url });
          setState(null);
          return;
        } catch (error) {
          if (error?.name === "AbortError") {
            setState(null);
            return;
          }
        }
      }
      await navigator.clipboard.writeText(url);
      setState("copied");
      setTimeout(() => setState(null), 2000);
    } catch (error) {
      setState("error");
      setTimeout(() => setState(null), 3000);
    }
  };

  const label = { loading: "...", copied: "Kopyalandı ✓", error: "Alınmadı" }[state] || "Paylaş";

  return (
    <button
      type="button"
      onClick={share}
      disabled={state === "loading"}
      className="text-emerald-600 hover:underline disabled:opacity-50"
    >
      {label}
    </button>
  );
};

const TicketTable = ({
  eventId,
  tickets = [],
  // The organizer may only touch their own invitations and partner tickets
  canEdit = () => true,
  canDelete = true,
  // The door checks people in; a ticket's QR is not theirs to open or pass on
  canOpenTicket = true,
  showMoney = true,
  onDelete = () => {},
  ticketTypeFilter,
  setTicketTypeFilter,
  statusFilter,
  paidByFilter,
  setStatusFilter,
  setPaidByFilter,
}) => {
  // The event's own question, when tickets carry an answer
  const customLabel = useMemo(() => tickets.find((t) => t?.customFieldLabel)?.customFieldLabel || null, [tickets]);
  const customValues = useMemo(
    () => [...new Set(tickets.map((t) => t?.customFieldValue).filter(Boolean))].sort((a, b) => a.localeCompare(b, "az")),
    [tickets]
  );
  const [customFilter, setCustomFilter] = useState("");
  const [search, setSearch] = useState("");
  const hasVouchers = useMemo(() => tickets.some((t) => t?.voucherCode), [tickets]);

  // Everything one might look a ticket up by: the buyer, how to reach them, and
  // the seats it covers, written both as "A-12" and as the label on the ticket.
  const haystack = (ticket) => [
    ticket?.name,
    ticket?.email,
    ticket?.phone,
    seatsLabel(ticket),
    ticket?.seatLabel,
    ...(ticket?.seats || []).map(({ label }) => label),
  ].filter(Boolean).join(" ").toLocaleLowerCase("az");

  // The door can do none of these, so they get no column of empty space
  const showActions = canOpenTicket || canDelete;

  const filteredTickets = useMemo(() => {
    const query = search.trim().toLocaleLowerCase("az");
    return tickets.filter((t) => {
      const matchesSearch = query ? haystack(t).includes(query) : true;
      const matchesType = ticketTypeFilter
        ? t.ticketTypeId?._id === ticketTypeFilter
        : true;
      const matchesStatus = statusFilter
        ? statusFilter === "null"
          ? t.paymentStatus == null
          : t.paymentStatus === statusFilter
        : true;
      const paidByType = paidByFilter
        ? t.paidBy === paidByFilter
        : true;
      const matchesCustom = customFilter ? t.customFieldValue === customFilter : true;
      return matchesSearch && matchesType && matchesStatus && paidByType && matchesCustom;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets, ticketTypeFilter, statusFilter, paidByFilter, customFilter, search]);

  const uniqueTicketTypes = useMemo(() => {
    return tickets.reduce((acc, item) => {
      acc[item?.ticketTypeId?._id] = item?.ticketTypeId?.name?.az;
      return acc;
    }, {});
  }, [tickets]);

  // rounded: adding prices like 11.85 leaves 2506.9999999999986 otherwise
  const sumOfPrices = Math.round(
    filteredTickets.filter(item => !unpaidTypes.includes(item?.paidBy)).reduce(
      (acc, item) => acc + paidAmount(item),
      0
    ) * 100
  ) / 100;

  // An order of several is several tickets, as everywhere else
  const countOfTickets = filteredTickets.reduce((sum, ticket) => sum + ticketCount(ticket), 0);

  // Seat column only for events that sell seats
  const hasSeats = useMemo(() => tickets.some((t) => t?.seatNumber || t?.seats?.length), [tickets]);

  return (
    <div className="mt-4 space-y-4">
      {/* Find one ticket: by the buyer, or by the seat they are sitting in */}
      <div className="relative">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={hasSeats ? "Ad, email, telefon və ya yer (məs. A-12)" : "Ad, email və ya telefon"}
          aria-label="Biletlərdə axtar"
          className="border p-2 pl-9 rounded w-full"
        />
        <span aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            aria-label="Axtarışı təmizlə"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
          >
            ✕
          </button>
        )}
      </div>

      {/* Filters */}
      <div className={`grid grid-cols-1 gap-4 ${customLabel ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-3"}`}>
        <select
          className="border p-2 rounded w-full"
          onChange={(e) => setTicketTypeFilter(e.target.value)}
          value={ticketTypeFilter}
        >
          <option value="">Bütün növlər</option>
          {Object.keys(uniqueTicketTypes).map((id) => (
            <option key={id} value={id}>
              {uniqueTicketTypes[id]}
            </option>
          ))}
        </select>

        <select
          className="border p-2 rounded w-full"
          onChange={(e) => setStatusFilter(e.target.value)}
          value={statusFilter}
        >
          <option value="">Bütün statuslar</option>
          <option value="success">Uğurlu</option>
          <option value="failed">Uğursuz</option>
          <option value="cancelled">Ləğv edilib (geri qaytarılıb)</option>
          <option value="null">Naməlum</option>
        </select>

        <select
          className="border p-2 rounded w-full"
          onChange={(e) => setPaidByFilter(e.target.value)}
          value={paidByFilter}
        >
          <option value="">Ödəmə növləri</option>
          {paidBy?.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>

        {customLabel && (
          <select
            className="border p-2 rounded w-full"
            aria-label={customLabel}
            onChange={(e) => setCustomFilter(e.target.value)}
            value={customFilter}
          >
            <option value="">{customLabel}: hamısı</option>
            {customValues.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Summary */}
      <div className="text-sm text-gray-600">
        <span className="mr-4">Alıcı sayı: <strong>{countOfTickets}</strong></span>
        {showMoney && <span>Cəmi məbləğ: <strong>{sumOfPrices} ₼</strong></span>}
      </div>

      {/* Tickets List - Responsive */}
      <div className="w-full overflow-x-auto rounded-xl shadow">
        <table className="min-w-max w-full bg-white">
          <thead>
            <tr className="bg-gray-200 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">
              <th className="p-4">Alıcı ({countOfTickets})</th>
              <th className="p-4">Bilet Növü</th>
              {hasSeats && <th className="p-4">Yer</th>}
              {customLabel && <th className="p-4">{customLabel}</th>}
              {showMoney && <th className="p-4">Bilet qiyməti</th>}
              {showMoney && hasVouchers && <th className="p-4">Promokod</th>}
              {showMoney && <th className="p-4">Ödənilən ({sumOfPrices}) ₼</th>}
              <th className="p-4">Status</th>
              <th className="p-4">Ödəmə növü</th>
              <th className="p-4">Tarix</th>
              {showActions && <th className="p-4">Əməliyyatlar</th>}
            </tr>
          </thead>
          <tbody>
            {filteredTickets.map((ticket) => (
              <tr key={ticket._id} className="border-t text-sm hover:bg-gray-50 whitespace-nowrap">
                <td className="p-4">
                  {ticket?.name}
                  {ticketCount(ticket) > 1 && (
                    <span className="ml-2 text-xs font-semibold text-accent-700 bg-accent-50 rounded-lg px-1.5 py-0.5">
                      {ticketCount(ticket)} bilet
                    </span>
                  )}
                </td>
                <td className="p-4">{ticket?.ticketTypeId?.name?.az}</td>
                {hasSeats && (
                  <td className="p-4">{seatsLabel(ticket) || "—"}</td>
                )}
                {customLabel && <td className="p-4">{ticket?.customFieldValue || "—"}</td>}
                {showMoney && <td className="p-4 text-gray-500">{ticket?.ticketTypeId?.price} ₼</td>}
                {showMoney && hasVouchers && (
                  <td className="p-4">
                    {ticket?.voucherCode ? (
                      <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 rounded-lg px-2 py-1 text-xs font-semibold">
                        🎟️ {ticket.voucherCode}
                        {ticket?.voucherDiscount > 0 && <span className="font-normal">-{ticket.voucherDiscount} ₼</span>}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                )}
                {showMoney && <td className="p-4 font-medium">{paidAmount(ticket)} ₼</td>}
                <td className="p-4">
                  {ticket?.paymentStatus === "cancelled" ? (
                    <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 rounded-lg px-2 py-1 text-xs font-semibold">
                      ləğv edilib
                    </span>
                  ) : (
                    ticket?.paymentStatus ?? "naməlum"
                  )}
                </td>
                <td className="p-4">{ticket?.paidBy}</td>
                <td className="p-4">{ticket?.createdAt}</td>
                {showActions && (
                <td className="p-4 space-x-2">
                  {canOpenTicket && (
                    <Link to={ticket?.qrCodeUrl} replace className="text-blue-600 hover:underline">Bax</Link>
                  )}
                  {canOpenTicket && ticket?.paymentStatus === "success" && (
                    <ShareTicketButton eventId={eventId} ticket={ticket} />
                  )}
                  {canEdit(ticket) && (
                    <Link to={`/dashboard/${eventId}/tickets/${ticket?._id}`} className="text-yellow-600 hover:underline">Düzəlt</Link>
                  )}
                  {canDelete && (
                    <button onClick={() => onDelete(eventId, ticket?._id)} className="text-red-600 hover:underline">Sil</button>
                  )}
                </td>
                )}
              </tr>
            ))}
            {filteredTickets.length === 0 && (
              <tr>
                <td colSpan={4 + (showActions ? 1 : 0) + (showMoney ? 2 : 0) + (hasSeats ? 1 : 0) + (customLabel ? 1 : 0) + (showMoney && hasVouchers ? 1 : 0)} className="text-center p-4 text-gray-500">
                  {search ? `«${search}» üzrə bilet tapılmadı.` : "Heç bir nəticə tapılmadı."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

    </div>
  );
};

export default TicketTable;
